/**
 * `isDatabaseDdl` — "did this script change the set of DATABASES the connection
 * can list?", the predicate behind dropping the shared database-listing cache
 * (`ddlInvalidator.invalidateAfterDdl` → `invalidateDatabaseListing`).
 *
 * Its own module, split out of `ddlDetector.ts`: unlike its siblings there this
 * one is engine-parameterised, reads the WHOLE script rather than a 2 KB prefix,
 * and carries a dynamic-SQL classification of its own — three concerns none of
 * the routine/table/schema predicates share.
 */

import type { DatabaseEngineId } from '@/core/engines';

import {
  maskCommentsOnly,
  maskCommentsThroughStrings,
  maskStringsAndComments,
  type MaskOptions,
} from './sqlTextMasker';

/**
 * Matches the DDL that changes WHICH DATABASES a connection can list — the set
 * every database picker renders (`useDatabaseList`, reading the shared
 * `['database-objects','databases',connectionId]` slot).
 *
 * Unlike the predicates in `ddlDetector.ts` this one is engine-parameterised. The
 * POLICY is identical everywhere ("after a DDL statement executes successfully,
 * invalidate the connection's database listing"); only the statement that adds or
 * removes a "database" differs, which is exactly the engine-internals exception
 * the cross-engine rule allows:
 *
 *  - **MSSQL / PostgreSQL** — `CREATE|ALTER|DROP DATABASE`; on MSSQL also
 *    `RESTORE DATABASE` and `DBCC CLONEDATABASE` (see {@link DATABASE_DDL_VERB} /
 *    {@link DATABASE_DDL_EXTRA_RE}).
 *  - **MySQL** — `SCHEMA` is a documented SYNONYM for `DATABASE`, so
 *    `CREATE SCHEMA x` creates a database. PostgreSQL's `CREATE SCHEMA` creates
 *    a namespace INSIDE the current database and must NOT match; that is why the
 *    object keyword is chosen per engine instead of one permissive alternation.
 *  - **Oracle** — the explorer lists `ALL_USERS` as its "databases"
 *    (`oracleQueries.databases`), so `CREATE USER` / `DROP USER` is what changes
 *    that list. `CREATE USER` exists on MSSQL and MySQL too, where it makes a
 *    principal / an account and no database — so it counts only on Oracle.
 *    12c+ `CREATE PLUGGABLE DATABASE` is covered by the optional modifier.
 *  - **SQLite** — the listing is the literal `main`, so nothing can change it;
 *    the pattern is simply inert there.
 *  - **Kusto** — `.create database x` matches, `.` being a word boundary.
 *
 * Oracle's `CREATE DATABASE LINK` creates a link, not a database — excluded by
 * the lookahead so it cannot trigger a pointless re-listing.
 */
const DATABASE_DDL_OBJECT = {
  mysql: '(DATABASE(?!\\s+LINK\\b)|SCHEMA)',
  oracle: '(PLUGGABLE\\s+)?(DATABASE(?!\\s+LINK\\b)|USER)',
  default: 'DATABASE(?!\\s+LINK\\b)',
} as const;

/**
 * `RESTORE` joins CREATE/ALTER/DROP: T-SQL's `RESTORE DATABASE x FROM DISK=…`
 * creates the database when it does not exist and replaces it when it does, so
 * the listing changes either way. Inert on the engines that have no such
 * statement — a shared verb list rather than a per-engine one, because the word
 * cannot appear in that position in their SQL anyway.
 */
const DATABASE_DDL_VERB = 'CREATE|ALTER|DROP|RESTORE';

/**
 * Database-changing statements that do NOT have the verb + object shape.
 * `DBCC CLONEDATABASE('src', 'clone')` (T-SQL) produces a new database. Same
 * reasoning as {@link DATABASE_DDL_VERB}: engine-specific syntax, safe to test
 * everywhere.
 */
const DATABASE_DDL_EXTRA_RE = /\bDBCC\s+CLONEDATABASE\b/i;

function databaseDdlPattern(engine: DatabaseEngineId | undefined): RegExp {
  const object =
    engine === 'mysql'
      ? DATABASE_DDL_OBJECT.mysql
      : engine === 'oracle'
        ? DATABASE_DDL_OBJECT.oracle
        : DATABASE_DDL_OBJECT.default;
  return new RegExp(`\\b(${DATABASE_DDL_VERB})\\s+${object}\\b`, 'i');
}

/** Either shape of database DDL — the verb + object pattern, or the odd one out. */
const carriesDatabaseDdl = (text: string, pattern: RegExp): boolean =>
  pattern.test(text) || DATABASE_DDL_EXTRA_RE.test(text);

/**
 * Constructs that run a STRING as SQL. Matched against text whose STRINGS are
 * intact, because the statement they carry lives inside a string literal the full
 * masker erases — which is exactly why they need naming:
 * `EXEC(N'CREATE DATABASE reporting')` is a perfectly ordinary way to create a
 * database on SQL Server, and masking made it invisible.
 *
 * One pattern for every engine rather than a per-engine table: each spelling is
 * syntax only its own engine has, so it cannot match another engine's script.
 * T-SQL `EXEC(…)` / `EXEC sp_executesql`, Oracle `EXECUTE IMMEDIATE` /
 * `DBMS_SQL`, PL/pgSQL `EXECUTE '…'` / `EXECUTE format(…)`, MySQL
 * `PREPARE … FROM '…'`.
 *
 * The OPERAND spellings have to be as complete as the constructs, because this is
 * a gate: miss the operand and the branch behind it never runs. PL/pgSQL executes
 * a string however PostgreSQL can spell one, so `EXECUTE` is recognised in front
 * of all three of its literal forms —
 * `EXECUTE 'CREATE DATABASE x'` (plain, already covered by `N?'`),
 * `EXECUTE E'CREATE DATABASE x'` (escape string) and
 * `EXECUTE $sql$ALTER DATABASE a RENAME TO b$sql$` (dollar-quoted, the form a
 * `DO $body$ … $body$` block normally uses so its inner literal needs no
 * doubling). The masker has always understood those spellings; only the gate in
 * front of it did not, and `DO $body$ BEGIN EXECUTE $sql$…$sql$; END $body$;`
 * therefore left the cached listing stale.
 */
const DYNAMIC_EXECUTION_RE =
  /\b(?:EXEC(?:UTE)?\s*\(|EXEC(?:UTE)?\s+IMMEDIATE\b|EXEC(?:UTE)?\s+(?:[NE]?'|\$|FORMAT\b|@)|SP_EXECUTESQL\b|DBMS_SQL\b|PREPARE\b)/i;

/**
 * Upper bound on the text this predicate will MASK. Past it the character walks are
 * skipped and — if the cheap pre-filter below has not already ruled the script out
 * — the answer is `true`. See {@link isDatabaseDdl}.
 *
 * Generous on purpose: it exists only so a pathological buffer (a query tab can
 * legitimately hold a multi-hundred-MB script — see the 1.4.26 heap fix) cannot
 * turn a character walk into a stall. Real scripts are orders of magnitude below
 * it.
 */
const DATABASE_DDL_SCAN_CAP = 1_000_000;

/**
 * Cheap pre-filter: every pattern above needs one of these words, and masking
 * only ever replaces characters with spaces — it can never introduce a word that
 * is not in the raw text. So a script without any of them cannot be database DDL,
 * and does not need the masking pass.
 *
 * Worth having because `invalidateAfterDdl` runs after EVERY successful
 * execution, and this predicate reads the whole script rather than a 2 KB prefix.
 * Ordinary queries therefore cost one regex, not a character walk.
 *
 * It runs BEFORE {@link DATABASE_DDL_SCAN_CAP}, and that ordering is the point: the
 * pre-filter is sound in the negative direction at ANY size, so a large ORDINARY
 * script (a 1.2 MB data load, a generated migration full of INSERTs) is classified
 * negative cheaply instead of tripping the cap's conservative `true` and
 * invalidating the connection's database listing for nothing. What the cap protects
 * against is a JS character walk over a multi-hundred-MB buffer; one linear regex
 * pass in native code is a different order of cost.
 */
const DATABASE_DDL_KEYWORD_RE = /\b(DATABASE|SCHEMA|USER|CLONEDATABASE)\b/i;

/**
 * Returns `true` when the SQL text looks like DDL that changes the connection's
 * database list.
 *
 * THE CORRECTNESS BOUND, stated plainly — the per-engine table above is about
 * *which statement counts*, not about when this predicate is right:
 *
 *  - It scans the WHOLE script, not a prefix: the query-execution pipeline hands
 *    it everything the user pressed Execute on, so a `CREATE DATABASE` behind a
 *    licence header or three preceding statements must still count.
 *  - It is a REGEX over masked text, not a parse. So it errs on the side of
 *    invalidating: a spurious invalidation costs exactly one extra listing, a
 *    missed one leaves the user looking at a stale database list. Every place it
 *    cannot decide therefore answers `true` — a script past
 *    {@link DATABASE_DDL_SCAN_CAP} that still contains a database-DDL keyword, and
 *    a dynamic-execution script with comment syntax inside a string literal
 *    (step 3 below).
 *  - A script with NONE of the keywords is `false` at any size: the pre-filter is
 *    checked before the cap, so an ordinary 1.2 MB data-load script costs one regex
 *    and invalidates nothing.
 *  - Masking still rules out the ordinary false positive: `SELECT 'CREATE
 *    DATABASE x' AS note` is a string value, not DDL, and no dynamic-execution
 *    construct is anywhere near it.
 *
 * EVERY PASS IS LINEAR IN THE SCRIPT LENGTH, and that is a requirement rather
 * than an observation: this runs synchronously in the renderer after every
 * successful execution. The three masking passes are single character walks, and
 * the four regexes are literal alternations whose quantifiers are bounded runs of
 * whitespace. The shape that is banned here is a delimited `/* … *\/` regex over
 * the whole script: with one opener per 3 characters and no closer anywhere
 * (`SELECT '` + `'/*x'.repeat(250_000)` + `' … DATABASE`, a valid string literal
 * under the cap) each opener rescans the whole suffix, which measured 26 s of
 * frozen renderer. `databaseDdlDetector.perf.test.ts` holds the bound.
 *
 * THE DYNAMIC-SQL BRANCH, in the order it asks its questions:
 *
 *  1. Does anything here EXECUTE a string at all (`DYNAMIC_EXECUTION_RE`)? Asked
 *     of comment-normalised text with the strings intact, because a comment can
 *     sit inside the construct itself: `EXEC/*a*\/(N'…')` is valid T-SQL and a raw
 *     regex misses it — and so is `EXEC/*a/*b*\/c*\/(N'…')`, which is why WHERE a
 *     block comment ends is read from the engine's grammar rather than at the first
 *     closer (`DelimiterPolicy.nestedBlockComments`; T-SQL and PostgreSQL nest,
 *     MySQL / Oracle / SQLite do not). A `no` ends the predicate — nothing after
 *     this point runs for an ordinary script that merely mentions a database name
 *     in a string.
 *  2. Does the executed text carry database DDL? Asked TWICE, and the answer is
 *     the OR: once of the comment-normalised text as-is, and once with the comment
 *     syntax INSIDE each string literal squashed as well
 *     (`maskCommentsThroughStrings` — `EXEC(N'CREATE/**\/DATABASE r')` needs it).
 *     Two passes rather than one because normalisation must never be able to
 *     DELETE executable text: it only ever adds a `true`. The pass that deleted
 *     text was the boundary-blind `/* … *\/` regex this replaced —
 *     `SELECT '/*'; EXEC(N'CREATE DATABASE reporting'); SELECT '*\/';` erased the
 *     EXEC in the middle and missed a real `CREATE DATABASE`.
 *  3. Still no match, but a string literal carried comment syntax at all? Then a
 *     statement this script may be about to EXECUTE has a comment in it, the squash
 *     deliberately restores the tail of an unclosed opener rather than blanking it
 *     (see `squashCommentSyntax`), and the reading that decides is one this regex
 *     cannot do. So it invalidates: a spurious listing costs one query, a miss shows
 *     the user a stale list.
 *
 *     `throughStrings !== commentless` is that test, and it is DELIBERATELY coarser
 *     than "the squash could not resolve it": the two texts differ wherever ANY
 *     `--` or `/*` sits inside ANY literal, resolvable or not, so
 *     `EXEC @rc = dbo.p; SELECT 'x -- y' AS [user]` answers `true`. That is the
 *     conservative direction and it costs nothing — the alternative is threading a
 *     per-span "unresolved" flag out of the masker to save an `invalidateQueries`
 *     that, with no observer on the key, refetches nothing.
 *
 * A statement commented out ENTIRELY stops counting, which is correct — it never
 * ran.
 *
 * WHAT IT DOES NOT SEE:
 *  - Dynamic SQL assembled so that the verb and the object keyword are never
 *    adjacent in the text and no comment explains the gap
 *    (`EXEC('CREATE ' + 'DATABASE x')`). Deciding that needs an evaluator, and
 *    treating "dynamic execution + a database keyword anywhere" as DDL would
 *    invalidate after every `EXEC(@s)` in a script that also selects from
 *    `sys.databases`.
 *  - A database created by something that is not in this text at all — another
 *    client, an Agent job, a different tab.
 *
 * Both gaps are covered by the revalidation every picker open runs
 * (`useDatabaseList`, `staleTime: 0`), which is also why a missed invalidation is
 * bounded by the Explorer's 30 s `staleTime` window rather than being permanent.
 */
export function isDatabaseDdl(
  sql: string,
  engine?: DatabaseEngineId,
  options?: Omit<MaskOptions, 'engine'>,
): boolean {
  // The sound cheap NEGATIVE first, at any size: no keyword ⇒ not database DDL.
  if (!DATABASE_DDL_KEYWORD_RE.test(sql)) return false;
  // A keyword IS present but the text is too large to mask — cannot classify what
  // we will not walk, so invalidate.
  if (sql.length > DATABASE_DDL_SCAN_CAP) return true;
  const maskOptions: MaskOptions = { ...options, engine };
  const pattern = databaseDdlPattern(engine);
  if (carriesDatabaseDdl(maskStringsAndComments(sql, maskOptions), pattern)) return true;
  // Nothing matched once strings and comments were erased, so any remaining match
  // is inside a string: a plain value (not DDL) or a dynamically executed statement
  // (DDL). Only a parser could tell them apart, so the presence of a
  // dynamic-execution construct decides it — and if there is none, stop here rather
  // than normalising comments the answer cannot depend on.
  const commentless = maskCommentsOnly(sql, maskOptions);
  if (!DYNAMIC_EXECUTION_RE.test(commentless)) return false;
  if (carriesDatabaseDdl(commentless, pattern)) return true;
  const throughStrings = maskCommentsThroughStrings(sql, maskOptions);
  if (carriesDatabaseDdl(throughStrings, pattern)) return true;
  // A literal carried comment syntax — anywhere, of any shape. Invalidate rather
  // than reason about what the executed statement reads as (step 3 above).
  return throughStrings !== commentless;
}
